import React from 'react'
import { useTranslation } from 'react-i18next'

import {
  ItemSelector,
  type ItemSelectorProps,
} from '~/components/elements/ItemSelector'
import {
  findByPath,
  formatNineSegment,
  type NineSegment,
  reversedNineSegments,
} from '~/utils'

export type NineSegmentSelectorProps = Omit<
  ItemSelectorProps,
  'data' | 'dataItemKey' | 'onChange' | 'textField' | 'value'
> & {
  onChange?: (nineSegment: NineSegment) => void
  value: NineSegment
}

export const NineSegmentSelector = React.forwardRef(
  (
    { onChange, value: valueProp, ...rest }: NineSegmentSelectorProps,
    ref: React.ForwardedRef<HTMLSpanElement>,
  ) => {
    const { t } = useTranslation()
    const data = React.useMemo(
      () =>
        reversedNineSegments.map((nineSegment) => ({
          nineSegment,
          label: formatNineSegment(nineSegment, '%n.%t1', t),
        })),
      [t],
    )
    const value = React.useMemo(
      () => findByPath(data, 'nineSegment', valueProp),
      [data, valueProp],
    )

    const handleChange = React.useCallback<EH<ItemSelectorProps, 'onChange'>>(
      (dataItem: (typeof data)[number]) => {
        onChange?.(dataItem.nineSegment)
      },
      [onChange],
    )

    return (
      <ItemSelector
        ref={ref}
        data={data}
        dataItemKey="nineSegment"
        textField="label"
        value={value}
        onChange={handleChange}
        {...rest}
      />
    )
  },
)
